import React from 'react';
import type { DroppableProvided } from './types';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useDndA11yContext } from './DndA11yContext';

export interface DroppableProps {
  droppableId: string;
  isDropDisabled?: boolean;
  direction?: 'vertical' | 'horizontal';
  type?: string;
  children: (provided: DroppableProvided) => any;
}

export function Droppable(props: DroppableProps) {
  const a11y = useDndA11yContext();
  const elementRef = React.useRef<HTMLElement | null>(null);
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);

  React.useEffect(() => {
    a11y?.registerDroppable(props.droppableId, props.isDropDisabled);
    return () => a11y?.unregisterDroppable(props.droppableId);
  }, [a11y, props.droppableId, props.isDropDisabled]);

  React.useEffect(() => {
    if (!elementRef.current) {
      return;
    }

    return dropTargetForElements({
      element: elementRef.current,
      canDrop: () => !props.isDropDisabled,
      getData: () => ({ droppableId: props.droppableId }),
      onDragEnter: () => {
        if (!props.isDropDisabled) {
          setIsDraggingOver(true);
        }
      },
      onDragLeave: () => {
        setIsDraggingOver(false);
      },
      onDrop: () => {
        setIsDraggingOver(false);
      },
    });
  }, [props.droppableId, props.isDropDisabled]);

  const provided: DroppableProvided = React.useMemo(
    () => ({
      droppableProps: {
        'data-eb-droppable-id': props.droppableId,
      },
      innerRef: (element) => {
        elementRef.current = element;
      },
      placeholder: null,
      isDraggingOver,
    }),
    [props.droppableId, isDraggingOver]
  );

  return props.children(provided) as any;
}
